import {Text} from '@mantine/core';
import React from 'react';

import {AnalyticsMetadataForContext} from 'analytics/AnalyticsTypes';
import * as ArchiveModal from 'archive/components/modals/ArchiveModal';
import coreObjectIsArchivable from 'archive/isArchivable';
import isArchived from 'archive/isArchived';
import Option from 'ui-library/menus/Option';

import {ArchiveModalItem, ArchiveOptionsForModal} from '../modals/ArchiveModal';

export interface ArchiveMenuOptionProps {
  isArchivable?: boolean;
  item: ArchiveModalItem;
  onArchive: (itemIds: Array<string>, purpose: string, options?: ArchiveOptionsForModal) => void;
  getAnalyticsMetadata?: () => AnalyticsMetadataForContext;
}

/**
 * Builds the Archive action as a Menu `Option` (for overflow "More" menus) rather than a standalone
 * `Button`. Selecting it opens `ArchiveModal`. Returns `null` when the item is already archived or
 * cannot be archived.
 *
 * This is a builder function, not a component, so the returned element is a real `Option` that a `Menu`
 * can introspect and clone as a direct child.
 */
export default function archiveMenuOption({
  isArchivable = false,
  item,
  onArchive,
  getAnalyticsMetadata,
}: ArchiveMenuOptionProps): JSX.Element | null {
  if (isArchived(item) || !coreObjectIsArchivable(item) || !isArchivable) {
    return null;
  }
  return (
    <Option
      key="archive"
      onSelect={() => {
        ArchiveModal.open({
          items: [item],
          onArchive,
          analyticsMetadata: getAnalyticsMetadata?.(),
        });
      }}
      dataTestAttrs={{component: 'ArchiveMenuOption', element: 'archive'}}
    >
      {/* Archive is destructive, so its menu entry is styled red to match the equivalent action
          in the expanded folder toolbar's More menu. */}
      <Text span={true} c="red.8">
        Archive
      </Text>
    </Option>
  );
}
